import _, { merge } from 'lodash'
import { v4 as uuid } from 'uuid'
import { List, Map, fromJS } from 'immutable'
import { getElementDataRoot } from '../constants/getElementDataRoot'
import { LAYOUT_MODE } from '../constants/toUnits'
import { updateOldValue } from './processPortalRules'
import { TAB_TYPES } from './tab-utils'
import { IS_WORKER } from '../constants/isWorker'

export const collectAllChildren = (children, path, state) => {
  const allChildren = [...children]

  _.each(children, (uid) => {
    const elemChild = state.getIn([...path, 'elements', uid, 'children'], List()).toJS()
    if (_.isArray(elemChild)) {
      allChildren.push(...elemChild)
      allChildren.push(...collectAllChildren(elemChild, path, state))
    }
  })

  return allChildren
}

export const getChildUIDs = (state, uid, checkedList = []) => {
  if (checkedList.includes(uid)) {
    return []
  }
  let type = state.getIn(['pages', state.get('page'), 'Props', 'elements', uid, 'type'])
  if (TAB_TYPES.indexOf(type) > -1) {
    let elem = state.getIn(['pages', state.get('page'), 'Props', 'elements', uid])
    let result = []
    _.each(LAYOUT_MODE, (mode) => {
      const tabs = elem.getIn(['props', mode, 'tabs'], List())
      elem.setIn(
        ['props', mode, 'tabs'],
        tabs.map((tab) => {
          tab.get('children', List()).map((id) => {
            result = [...result, ...getChildUIDs(state, id, [...checkedList, uid])]
          })
        })
      )
    })
    return [uid, ..._.compact(result)]
  } else if (['Strip', 'Stack', 'Scroll', 'RepeatAutoFit'].includes(type)) {
    const uids = state.getIn(['pages', state.get('page'), 'Props', 'elements', uid, 'children'], List()).toJS()
    return [uid, ..._.flatMap(uids, (childUID) => getChildUIDs(state, childUID, [...checkedList, uid]))]
  } else {
    const uids = state.getIn(['pages', state.get('page'), 'Props', 'elements', uid, 'children'], List()).toJS()
    return [uid, ...checkedList, ...uids]
  }
}

const PLACE_MAP = {
  left: 'flex-start',
  top: 'flex-start',
  right: 'flex-end',
  bottom: 'flex-end',
  center: 'center',
  stretch: 'stretch',
}

export const getPlaceToStyle = (place = '') => {
  const [horizontal, vertical] = place.split('-')
  return {
    alignSelf: PLACE_MAP[vertical],
    justifySelf: PLACE_MAP[horizontal],
  }
}

export const copyChildElements = (state, copyUID, nextUID, elem, pageUID) => {
  let childElements = {}
  let childStyles = {}
  let nextElem = { ...elem }
  let styles = {}

  const ref = _.get(elem, 'props.styleRef', copyUID)
  _.set(nextElem, ['props', 'styleRef'], ref)
  const isProject =
    state.getIn(['project', 'Settings', 'elements'], Map()).filter((elem, elemUID) => elemUID === copyUID).size > 0
  const isGlobalStyle = state.getIn(['project', 'Settings', 'styles', ref, 'isGlobal'])
  const style = isGlobalStyle
    ? state.getIn(['project', 'Settings', 'styles', ref], Map())
    : state.getIn([...getElementDataRoot(isProject ? 'project' : pageUID), 'styles', ref], Map())
  styles[ref] = style.toJS()

  let children = nextElem.children
  if (nextElem.type == 'DrawerEl' || nextElem.type == 'Modal') {
    children = _.flatten(children)
  }
  nextElem.children = _.map(children, (uid) => {
    const childElem = state
      .getIn([...getElementDataRoot(isProject ? 'project' : pageUID), 'elements', uid], Map())
      ?.toJS?.()
    if (!childElem || !childElem.props) {
      return null
    }
    const childRef = childElem.props.styleRef
    const isChildGlobalStyle = state.getIn(['project', 'Settings', 'styles', childRef, 'isGlobal'])
    const style = isChildGlobalStyle
      ? state.getIn(['project', 'Settings', 'styles', childRef], Map())
      : state.getIn([...getElementDataRoot(isProject ? 'project' : pageUID), 'styles', childRef], Map())
    const nextChildUID = uid
    const {
      nextElem: _nextElem,
      childElements: _childElements,
      childStyles: _childStyles,
    } = copyChildElements(
      state,
      uid,
      nextChildUID,
      {
        ...childElem,
        parent: nextUID,
      },
      pageUID
    )

    childElements = {
      ...childElements,
      [nextChildUID]: _nextElem,
      ..._childElements,
    }
    childStyles = {
      ...childStyles,
      [childRef]: style.toJS(),
      ..._childStyles,
    }
    return nextChildUID
  })
  nextElem.children = _.compact(nextElem.children)
  return {
    nextElem,
    childElements,
    childStyles,
    styles,
  }
}

const getValueFor = (elem, device, pre, post, def) => {
  const lgValue = _.get(elem, [...pre, 'lg', ...post], def)
  const smValue = _.get(elem, [...pre, 'sm', ...post], null)
  const mdValue = _.get(elem, [...pre, 'md', ...post], null)
  const xsValue = _.get(elem, [...pre, 'xs', ...post], null)
  const ssValue = _.get(elem, [...pre, 'ss', ...post], null)

  const values = [lgValue, mdValue, smValue, xsValue, ssValue]
  let valueIndex = LAYOUT_MODE.indexOf(device)
  if (valueIndex !== -1) {
    let value = values[valueIndex]
    do {
      if (typeof value !== 'undefined' && value !== null) {
        return value
      }
      value = values[--valueIndex]
    } while (valueIndex >= 0)
  }
  return lgValue
}

const getWidgetStaticValues = (children, path, widgets, state) => {
  const layoutMode = state.getIn(['layoutMode'], 'lg')
  return _.reduce(
    children,
    (ret, child, uid) => {
      if ('Image' === child.type) {
        if (!state.getIn(['state', uid, 'IMG_ALT'])) {
          _.set(ret, `${uid}.IMG_ALT`, getValueFor(child, layoutMode, ['props'], ['alt'], ''))
        }
        if (!state.getIn(['state', uid, 'IMG_SRC'])) {
          _.set(ret, `${uid}.IMG_SRC`, getValueFor(child, layoutMode, ['props'], ['src'], ''))
        }
      } else if ('Button' === child.type) {
        if (!state.getIn(['state', uid, 'BTN_TEXT'])) {
          _.set(ret, `${uid}.BTN_TEXT`, getValueFor(child, layoutMode, ['props'], ['text'], ''))
        }
        if (!state.getIn(['state', uid, 'BTN_HREF'])) {
          _.set(ret, `${uid}.BTN_HREF`, getValueFor(child, layoutMode, ['props'], ['href'], '#'))
        }
      } else if ('Typography' === child.type) {
        if (!state.getIn(['state', uid, 'TYPO_TEXT'])) {
          _.set(ret, `${uid}.TYPO_TEXT`, getValueFor(child, layoutMode, ['props'], ['text'], ''))
        }
      } else if (child.type === 'Widget') {
        const widget = fromJS(child.props.data)
        if (widget) {
          return _.merge(
            ret,
            getWidgetStaticValues(widget.getIn(['Props', 'elements'], Map()).toJS(), `${uid}`, widgets, state)
          )
        }
      }
      return ret
    },
    {}
  )
}

export const setWidgetElemStaticVal = (state) => {
  let retState = state
  // if (retState.getIn(['widgets', 'current', 'Type']) === 'view') {
  //   const staticState = getWidgetStaticValues(
  //     retState.getIn(['widgets', 'current', 'Props', 'elements'], Map()).toJS(),
  //     retState.getIn(['widgets', 'current', '_id']),
  //     retState.getIn(['widgets', 'data'], Map()),
  //     retState
  //   )
  //   return retState.setIn(
  //     ['state'],
  //     retState.getIn(['state'], Map())
  //       .mergeDeep(fromJS(staticState))
  //   )
  // }
  return retState
}

export const rulesHashMap = {}
if (typeof window !== 'undefined' && !IS_WORKER) {
  window.rulesHashMap = rulesHashMap
}
export const setRule = (key, value, index, path) => {
  if (path) {
    if (_.has(rulesHashMap, `${[...path, key].join(':')}`)) {
      rulesHashMap[`${[...path, key].join(':')}`].push([value, index, path])
      rulesHashMap[`${[...path, key].join(':')}`] = _.uniqBy(rulesHashMap[`${[...path, key].join(':')}`], (v) =>
        _.isArray(v) ? v.join(':') : v
      )
    } else {
      rulesHashMap[`${[...path, key].join(':')}`] = [[value, index, path]]
    }
  } else {
    if (_.has(rulesHashMap, key)) {
      rulesHashMap[key].push([value, index])
      rulesHashMap[key] = _.uniqBy(rulesHashMap[key], (v) => (_.isArray(v) ? v.join(':') : v))
    } else {
      rulesHashMap[key] = [[value, index]]
    }
  }
}
export const getAllRules = (key) => {
  return rulesHashMap
}

export const setupRulesHashMap = (rules, uid, path, RepeatedStripsID = [], lib) => {
  _.each(rules, (currentRule, index) => {
    const processCondition = (cond) => {
      if (cond.field) {
        if (_.startsWith(cond.field.group, 'view:')) {
          setRule(cond.field.value, uid, index, path)
        } else if (cond.field && cond.field.value) {
          if (_.startsWith(cond.field.value, 'CWDB:')) {
            setRule(String(cond.field.value).replace('CWDB:', ''), uid, index, path)
          } else if (cond.field.group === 'params') {
            setRule(cond.field.value, uid, index, path)
          } else if (_.startsWith(cond.field.group, 'form:')) {
            setRule(cond.field.value, uid, index, path)
          } else {
            setRule(cond.field.value, uid, index, path)
          }
        } else {
          setRule(cond.field.value, uid, index, path)
        }
      }
      if (cond.valueType === 'field' && cond.value) {
        if ((_.startsWith(cond.value.group), 'form:')) {
          setRule(cond.value.value, uid, index, path)
        } else if (cond.value.group === 'params') {
          setRule(cond.value.value, uid, index, path)
        }
      }
      if (cond.value2Type === 'field' && cond.value2) {
        if ((_.startsWith(cond.value2.group), 'form:')) {
          setRule(cond.value2.value, uid, index, path)
        } else if (cond.value2.group === 'params') {
          setRule(cond.value2.value, uid, index, path)
        }
      }
    }
    if (currentRule?.modeType === 'flow') {
      let nodes = _.get(currentRule, ['actions', 'actionFlow', 0, 'list'], [])
      let startNode = _.find(nodes, (node) => node.type === 'input')
      if (startNode) {
        let connections = _.filter(nodes, (node) => node.source === startNode.id)
        let conditionNodes = _.reduce(
          connections,
          (r, connection) => {
            let node = _.find(nodes, (node) => node.id === connection.target) ?? null
            if (node) {
              return [...r, node]
            }
            return r
          },
          []
        )
        _.each(conditionNodes, (node) => {
          if (node.actionType === 'libFlow') {
            let target = node?.data?.libFlow?.[0]?.selected
            let action = lib?.[target]
            if (action) {
              setupRulesHashMap([action], uid, path, RepeatedStripsID, lib)
            }
          } else {
            _.each(_.get(node, 'data', []), (node) => {
              _.each(node.conditions, processCondition)
            })
          }
        })
      }
    } else {
      _.each(currentRule.conditions, processCondition)
    }
    _.each(currentRule.actions, (v, k) => {
      let pathKey = String(k).split(':')
      if (RepeatedStripsID.indexOf(pathKey[1]) !== -1) {
        if (pathKey.indexOf('ftIsSelVal') == -1) {
          setRule(_.slice(pathKey, 0, 2).join(':'), uid, index, path)
          setRule(_.slice(pathKey, 0, 2).join(':') + ':value', uid, index, path)
          setRule(_.slice(pathKey, 0, 2).join(':') + '-totalRows', uid, index, path)
          // setRule(k, uid, index, path)
        }
      }
    })
  })
}

export const getValueForMapping = (field, state, params, index) => {
  try {
    if (field && _.endsWith(field.value, ':ft_search')) {
      index = null
    }
    if (_.startsWith(field.group, 'pageParam')) {
      return _.get(state, String(field.value).split(':'), '')
    } else if (_.startsWith(field.group, 'PowerTableWidget:')) {
      const fieldkey = _.last(String(field.value).split('#-#')).split(/:/g)
      const path = [...fieldkey]
      let val = _.get(state, path, [])
      if (_.isArray(val)) {
        return _.get(val, index, [])
      } else {
        return val
      }
    } else if (_.startsWith(field.group, 'tableWidget:')) {
      const fieldkey = _.last(String(field.value).split('#-#')).split(/:/g)
      const path = [..._.initial(fieldkey), index, _.last(fieldkey)]
      return _.get(state, path, '')
    } else if (field.group === 'params' || field.group === 'param') {
      const keyPath = _.last(String(field.value).split('#-#')).split(/:/g)
      return _.get(params, keyPath, '')
    } else if (_.startsWith(field.group, 'formWidget:') || _.startsWith(field.group, 'listWidget:')) {
      /**
       * @todo show to Vitaliy
       * test in portal builder
       */
      if (String(field.value).indexOf(':ft_') > -1) {
        let val1 = _.get(state, String(field.value).split(':'))
        if (val1 !== undefined) {
          return val1
        }
      }
      let fieldID = String(field.value).split(':')
      fieldID.splice(0, 1)
      fieldID = fieldID.join('.')
      if (!fieldID) {
        return ''
      }

      let val = _.has(state, fieldID + '.value') ? _.get(state, fieldID + '.value', '') : _.get(state, fieldID, '')
      if (!val) {
        let fieldID2 = String(field.value).split(':')
        let val2 = _.has(state, [...fieldID2, 'value'])
          ? _.get(state, [...fieldID, 'value'], '')
          : _.get(state, fieldID2, '')
        if (val2) {
          return val2
        }
      }
      return val
    } else if (_.startsWith(field.group, 'form:')) {
      return _.has(state, field.value + '.value')
        ? _.get(state, field.value + '.value', '')
        : _.get(state, field.value, '')
    } else if (field.group === 'smartvReg') {
      return _.get(state, field.value, '')
    } else if (field.group === 'smartv') {
      if (typeof window !== 'undefined') {
        return _.get(window.__FT_APP_STATE__, `svParams.${field.value}`, '')
      } else {
        return ''
      }
    } else if (_.startsWith(field.group, 'view:')) {
      let fieldKey = String(field.value).split(':')
      if (fieldKey.length == 2) {
        index = null
      } else if (fieldKey.length == 3 && ['ftListValue', 'ftListLabel', 'ft_value', 'ft_text'].includes(fieldKey[2])) {
        index = null
      }
      if (field.type === 'Strip' && _.endsWith(field.value, ':ftSelVal')) {
        return _.get(state, String(field.value).split(':'), '')
      } else if (index !== undefined && index !== null) {
        if (
          field.subType == 'lookup' ||
          field.subType == 'radio' ||
          _.endsWith(field.value, 'startDate') ||
          _.endsWith(field.value, 'endDate')
        ) {
          let path = String(field.value).split(':')
          path = [..._.initial(path), index, _.last(path)]
          return _.get(state, path, '')
        } else {
          return _.get(state, [...String(updateOldValue(field.value)).split(':'), index], '')
        }
      } else {
        return _.get(state, String(field.value).split(':'), '')
      }
    } else {
      if (index !== undefined && index !== null) {
        return _.get(state, [...String(updateOldValue(field.value)).split(':'), index], '')
      } else {
        return _.get(state, String(field.value).split(':'), '')
      }
    }
  } catch (err) {
    console.error(err)
    return ''
  }
}
